import { Alert } from "@mui/material";
import * as React from "react";

import { MetadataForm, MetadataView } from "@/shared/components/Metadata";
import { useFileMetadata } from "@/shared/domain/files/hooks/useFileMetadata";
import { useMutateFileMetadata } from "@/shared/domain/files/hooks/useMutateFileMetadata";

import { Metadata, MetadataDiff } from "../Metadata/types";
import { AutocompleteType } from "../TagAndMetadataAutocomplete";

import { MetadataSkeleton } from "./MetadataSkeleton";

interface FileMetadataProps {
  fileId: string;
  readonly?: boolean;
}

export const FileMetadata: React.FC<FileMetadataProps> = ({
  fileId,
  readonly = false,
}) => {
  const metadataQuery = useFileMetadata(fileId);

  const metadata = metadataQuery.data;

  const updateFileMetadataMutation = useMutateFileMetadata();

  const onChangeMetadata = (updates: Metadata, diff?: MetadataDiff) => {
    if (updates && diff?.keysDeleted && diff.keysDeleted.length > 0) {
      updateFileMetadataMutation.mutate({
        fileId,
        removeFields: diff.keysDeleted,
      });
    }
    if (updates && diff?.keysUpdated && diff.keysUpdated.length > 0) {
      const keysToUpdate = diff.keysUpdated;
      const putFields: Record<string, unknown> = {};
      for (const k of keysToUpdate) {
        putFields[k] = updates[k];
      }

      updateFileMetadataMutation.mutate({
        fileId,
        putFields,
      });
    }
  };

  const onAddMetadata = (key: string, value: unknown) => {
    if (key && value) {
      const updates = {
        [key]: value,
      };

      updateFileMetadataMutation.mutate({
        fileId: fileId,
        putFields: updates,
      });
    }
  };

  if (metadataQuery.isLoading) {
    return <MetadataSkeleton />;
  }

  return (
    <>
      {readonly && metadata && Object.keys(metadata).length === 0 && <>None</>}
      {updateFileMetadataMutation.isError && (
        <Alert
          onClose={() => updateFileMetadataMutation.reset()}
          severity="error"
          sx={(theme) => ({ marginTop: theme.spacing(1) })}
        >
          {updateFileMetadataMutation.error.message}
        </Alert>
      )}
      <MetadataView
        metadata={metadata || {}}
        onChange={onChangeMetadata}
        disabled={readonly}
      />
      {readonly === false && (
        <MetadataForm
          metadata={metadata || {}}
          autocompleteType={AutocompleteType.FileMetadataKeys}
          onAddMetadata={onAddMetadata}
          disabled={false}
          isLoading={updateFileMetadataMutation.isPending}
        />
      )}
    </>
  );
};

// Removed useEagerMetadataUpdates as it is now handled by TanStack Query's optimistic updates.
